/**
 * The Skills library: work procedures (memory process nodes) and execution
 * skills (agent skills) normalized for display only. Each keeps its own
 * store's ID and kind; nothing here is sent back to either API.
 */
import { listSkills, type Skill } from "@/api/agents";
import type { KnowledgeNodeRecord } from "@/api/memory";
import { listWorkSkills } from "@/api/work-skills";
import { useRetryableLoad } from "@/hooks/use-retryable-load";

export type SkillLibraryRef =
  | {
      kind: "work";
      id: string;
      title: string;
      description: string;
      /** False for a private overlay ("Your version"). */
      shared: boolean;
    }
  | {
      kind: "agent";
      id: string;
      title: string;
      description: string;
      slug: string;
      builtin: boolean;
    };

export type SkillLibraryFilter = "all" | "yours" | "included" | "added";

/** Catalogue keys (settings-skills) of each kind's display name. */
export const SKILL_KIND_KEYS = {
  work: "kindWork",
  agent: "kindAgent",
} as const satisfies Record<SkillLibraryRef["kind"], string>;

export const rowKey = (skill: SkillLibraryRef) => `${skill.kind}:${skill.id}`;

/** Work refs; a private version without a summary borrows its library's. */
export function toWorkRefs(
  records: readonly KnowledgeNodeRecord[],
): SkillLibraryRef[] {
  const libraries = new Map(
    records
      .filter((item) => item.shared)
      .map((item) => [
        `${item.metadata.external_id}@${item.metadata.version}`,
        item,
      ]),
  );
  return records.map((record) => ({
    kind: "work",
    id: record.id,
    title: String(record.metadata.title ?? record.name),
    description: String(
      record.metadata.summary ??
        libraries.get(String(record.metadata.adapts))?.metadata.summary ??
        record.description ??
        "",
    ),
    shared: record.shared,
  }));
}

export function toAgentRef(skill: Skill): SkillLibraryRef {
  return {
    kind: "agent",
    id: skill.id,
    title: skill.name,
    description: skill.description,
    slug: skill.slug,
    builtin: skill.is_builtin,
  };
}

function matchesFilter(skill: SkillLibraryRef, filter: SkillLibraryFilter) {
  if (filter === "yours") return skill.kind === "work" && !skill.shared;
  if (filter === "included") return skill.kind === "agent" && skill.builtin;
  if (filter === "added") return skill.kind === "agent" && !skill.builtin;
  return true;
}

/** Display filtering only, ordered by title, then kind, then ID. */
export function filterLibrary(
  refs: readonly SkillLibraryRef[],
  { search, filter }: { search: string; filter: SkillLibraryFilter },
): SkillLibraryRef[] {
  const query = search.trim().toLowerCase();
  return refs
    .filter(
      (skill) =>
        matchesFilter(skill, filter) &&
        `${skill.title} ${skill.description}`.toLowerCase().includes(query),
    )
    .sort(
      (a, b) =>
        a.title.localeCompare(b.title) ||
        a.kind.localeCompare(b.kind) ||
        a.id.localeCompare(b.id),
    );
}

const NO_WORK_SKILLS: KnowledgeNodeRecord[] = [];
const NO_AGENT_SKILLS: Skill[] = [];

/** Both stores, loaded and retried independently. */
export function useSkillLibrary() {
  const { data: workItems, ...work } = useRetryableLoad<KnowledgeNodeRecord[]>(
    listWorkSkills,
    NO_WORK_SKILLS,
  );
  const { data: agentItems, ...agent } = useRetryableLoad<Skill[]>(
    listSkills,
    NO_AGENT_SKILLS,
  );
  const refs = [...toWorkRefs(workItems), ...agentItems.map(toAgentRef)];
  return { refs, work, agent };
}
